import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import TableRenderer from './TableRenderer';
import ChartRenderer from './ChartRenderer';

const DashboardPanel = ({
  panel,
  colors,
  onRefresh,
  onRemove,
  onTableRowAction,
  onTableBulkAction,
  onTableGlobalAction,
  onTableLinkClick,
  onTableConfigure,
  onTableFilterRemove,
  onTableAddFilter,
  onChartActionClick,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(panel.lastRefreshed || null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh(panel.panelId);
      setLastRefreshed(new Date().toISOString());
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatLastRefreshed = () => {
    if (!lastRefreshed) return null;
    const date = new Date(lastRefreshed);
    return `Last refreshed: ${date.toLocaleTimeString()}`;
  };

  const renderContent = () => {
    if (isRefreshing) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress size={32} sx={{ color: '#22c55e' }} />
        </Box>
      );
    }

    if (panel.type === 'TABLE' && panel.data?.table) {
      return (
        <TableRenderer
          table={panel.data.table}
          colors={colors}
          onRowAction={onTableRowAction}
          onBulkAction={onTableBulkAction}
          onGlobalAction={onTableGlobalAction}
          onLinkClick={onTableLinkClick}
          onConfigureTable={onTableConfigure}
          onFilterRemove={onTableFilterRemove}
          onAddFilter={onTableAddFilter}
        />
      );
    }

    if (panel.type === 'CHART' && panel.data?.chart) {
      return (
        <ChartRenderer
          chart={panel.data.chart}
          colors={colors}
          onActionClick={onChartActionClick}
        />
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          color: colors.textMuted,
        }}
      >
        <Typography variant="body2">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surfaceElevated,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: colors.textPrimary, fontWeight: 600 }}
          >
            {panel.title}
          </Typography>
          {panel.sourceQuery && (
            <Typography
              variant="caption"
              sx={{ color: colors.textMuted, display: 'block' }}
            >
              Query: {panel.sourceQuery}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Refresh panel">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                color: colors.textSecondary,
                '&:hover': {
                  color: '#22c55e',
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove from dashboard">
            <IconButton
              size="small"
              onClick={() => onRemove(panel.panelId)}
              sx={{
                color: colors.textSecondary,
                '&:hover': {
                  color: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Panel content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
        {renderContent()}
      </Box>

      {/* Panel footer */}
      {lastRefreshed && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: colors.background,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {formatLastRefreshed()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DashboardPanel;
